import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Brain, Sparkles, Target, Zap, CheckCircle2, ArrowRight, FileText, Monitor, Mic } from 'lucide-react';
import { Button, BrandName } from '../components/ui';
import { AuthPanel } from '../components/AuthPanel';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../firebase';
import { authService } from '../services/auth';
import { showToast } from '../utils/toast';
import { useTheme } from '../theme';

type AuthMode = 'signin' | 'signup' | 'forgot';

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { branding } = useTheme();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>('signin');
  const [user, setUser] = useState<User | null>(() => authService.getCurrentUser());

  const openAuth = (mode: AuthMode) => {
    setAuthMode(mode);
    setAuthOpen(true);
  };

  const handlePrimaryCta = () => {
    if (user) {
      navigate('/home');
      return;
    }
    openAuth('signup');
  };

  React.useEffect(() => {
    if (!auth) return;
    return onAuthStateChanged(auth, (u) => setUser(u));
  }, []);

  const handleLogout = async () => {
    try {
      await authService.signOut();
    } catch (error: any) {
      showToast.error(error?.message || 'Logout failed');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              {branding.logoUrl && (
                <img src={branding.logoUrl} alt="mAIthZen AI Math Tutor Logo" className="w-10 h-10 rounded-xl object-contain" />
              )}
              <span className="text-xl font-bold" style={{ color: branding.textColor }}>
                <BrandName text={branding.appTitle} />
              </span>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Features</button>
              <button onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Pricing</button>
            </div>

            <div className="flex items-center gap-3">
              {user ? (
                <>
                  <Link to="/home">
                    <Button variant="secondary" size="sm">Open App</Button>
                  </Link>
                  <Button variant="ghost" size="sm" onClick={handleLogout}>
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" size="sm" onClick={() => openAuth('signin')}>
                    Log in
                  </Button>
                  <Button variant="primary" size="sm" onClick={handlePrimaryCta}>
                    Try Now
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" aria-label="Hero - AI Math Tutor">
        <div className="max-w-4xl mx-auto text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
            style={{ backgroundColor: branding.secondaryColor, color: branding.primaryColor }}
          >
            <Sparkles className="w-4 h-4" />
            <span>Powered by Advanced AI</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight" style={{ color: branding.textColor }}>
            <span className="text-gray-900">Learn Math the </span>
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: `linear-gradient(to right, ${branding.primaryColor}, ${branding.primaryColorHover})` }}
            >
              Zen Way
            </span>
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-gray-700 max-w-2xl mx-auto leading-relaxed">
            Learn math calmly, clearly, and step by step — without stress or confusion.
          </p>

          <p className="mt-3 text-sm sm:text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            mAIthZen is your AI-powered math tutor app — get quick solutions, multiple approaches, key formulas, and a clear final answer.
          </p>
          
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button variant="primary" size="lg" className="px-8 py-3 text-base" onClick={handlePrimaryCta}>
              Try the AI Math Tutor
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <button onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}>
              <Button variant="secondary" size="lg" className="px-8 py-3 text-base">
                View pricing
              </Button>
            </button>
          </div>

          <p className="mt-4 text-sm text-gray-500">No credit card required • Free tier available</p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50" aria-labelledby="features-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="features-heading" className="text-3xl sm:text-4xl font-bold text-gray-900">Why Students Love {branding.appTitle}</h2>
            <p className="mt-4 text-lg text-gray-600">Everything you need to master math with our AI math tutor, in one place.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Brain className="w-6 h-6" />}
              title="Step-by-Step Solutions"
              description="Get detailed explanations for every step. Understand the 'why' behind each solution."
              branding={branding}
            />
            <FeatureCard
              icon={<Target className="w-6 h-6" />}
              title="Multiple Approaches"
              description="Learn different ways to solve the same problem. Find the method that clicks for you."
              branding={branding}
            />
            <FeatureCard
              icon={<Zap className="w-6 h-6" />}
              title="Instant Answers"
              description="No waiting. Get solutions in seconds, available 24/7 whenever you need help."
              branding={branding}
            />
            <FeatureCard
              icon={<FileText className="w-6 h-6" />}
              title="Question Explanation"
              description="Get a simple explanation of the question and what it’s asking—before you start solving."
              branding={branding}
            />
            <FeatureCard
              icon={<Monitor className="w-6 h-6" />}
              title="Blackboard Experience"
              description="A clean blackboard-style workspace to follow steps, formulas, and final answers with clarity."
              branding={branding}
            />
            <FeatureCard
              icon={<Mic className="w-6 h-6" />}
              title="Voice Explanation"
              description="Listen to step-by-step voice explanations—learn like a real classroom session."
              branding={branding}
            />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20" aria-labelledby="pricing-heading">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="pricing-heading" className="text-3xl sm:text-4xl font-bold text-gray-900">Simple, Transparent Pricing</h2>
            <p className="mt-4 text-lg text-gray-600">Start free, upgrade when you need more questions, or choose a tenant plan for schools.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <PricingCard
              name="Free"
              price="₹0"
              period="/month"
              description="Perfect for getting started"
              features={[
                '30 questions per month',
                'Step-by-step solutions',
                'Question explanation',
              ]}
              buttonText="Try free"
              buttonVariant="secondary"
              onGetStarted={handlePrimaryCta}
              branding={branding}
            />
            <PricingCard
              name="Plus"
              price=""
              period=""
              description="For students who practice daily"
              features={[
                '200 questions per user / month',
                'Multiple solution approaches',
                'Voice explanations',
                'Blackboard experience',
              ]}
              buttonText="Coming Soon"
              buttonVariant="secondary"
              highlighted
              onGetStarted={() => showToast.info('Plus plan coming soon!')}
              branding={branding}
            />
            <PricingCard
              name="Schools (B2B)"
              price=""
              period=""
              description="Tenant-based subscription for schools"
              features={[
                'Tenant-based setup & branding',
                'Custom question limits',
                'Onboarding & support',
              ]}
              buttonText="Coming Soon"
              buttonVariant="secondary"
              onGetStarted={() => showToast.info('B2B plan coming soon!')}
              branding={branding}
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3">
                {branding.logoUrl && (
                  <img src={branding.logoUrl} alt="mAIthZen AI Math Tutor" className="w-9 h-9 rounded-xl object-contain" />
                )}
                <span className="text-lg font-semibold" style={{ color: branding.textColor }}>
                  <BrandName text={branding.appTitle} />
                </span>
              </div>
              <p className="mt-3 text-sm text-gray-600 max-w-sm">
                mAIthZen — your AI-powered math tutor app. Get step-by-step solutions, multiple approaches, and instant feedback for any math problem.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-900">Product</h4>
              <ul className="mt-3 space-y-2 text-sm">
                <li><a href="#features" className="text-gray-600 hover:text-gray-900">Features</a></li>
                <li><a href="#pricing" className="text-gray-600 hover:text-gray-900">Pricing</a></li>
                <li><Link to="/home" className="text-gray-600 hover:text-gray-900">Open App</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-900">Account</h4>
              <ul className="mt-3 space-y-2 text-sm">
                <li><button onClick={() => openAuth('signin')} className="text-gray-600 hover:text-gray-900">Sign in</button></li>
                <li><button onClick={() => openAuth('signup')} className="text-gray-600 hover:text-gray-900">Create account</button></li>
                <li><button onClick={() => openAuth('forgot')} className="text-gray-600 hover:text-gray-900">Reset password</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-900">Legal</h4>
              <ul className="mt-3 space-y-2 text-sm">
                <li><button type="button" className="text-gray-600 hover:text-gray-900">Privacy</button></li>
                <li><button type="button" className="text-gray-600 hover:text-gray-900">Terms</button></li>
                <li><button type="button" className="text-gray-600 hover:text-gray-900">Contact</button></li>
              </ul>
            </div>
          </div>

          <div className="mt-10 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-500">© {new Date().getFullYear()} {branding.appTitle}. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Auth Panel */}
      <AuthPanel isOpen={authOpen} onClose={() => setAuthOpen(false)} initialMode={authMode} />
    </div>
  );
};

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const FeatureCard: React.FC<FeatureCardProps & { branding: { primaryColor: string; primaryColorHover: string; textColor: string; textColorMuted: string } }> = ({ icon, title, description, branding }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
    <div
      className="w-12 h-12 rounded-xl flex items-center justify-center text-white mb-4"
      style={{ background: `linear-gradient(to bottom right, ${branding.primaryColor}, ${branding.primaryColorHover})` }}
    >
      {icon}
    </div>
    <h3 className="text-lg font-semibold mb-2" style={{ color: branding.textColor }}>{title}</h3>
    <p style={{ color: branding.textColorMuted }}>{description}</p>
  </div>
);

interface PricingCardProps {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  buttonText: string;
  buttonVariant: 'primary' | 'secondary';
  highlighted?: boolean;
  onGetStarted: () => void;
  branding: { primaryColor: string; secondaryColor: string; textColor: string; textColorMuted: string };
}

const PricingCard: React.FC<PricingCardProps> = ({
  name,
  price,
  period,
  description,
  features,
  buttonText,
  buttonVariant,
  highlighted,
  onGetStarted,
  branding,
}) => (
  <div
    className="rounded-2xl p-8 bg-white border border-gray-200 shadow-sm transition-all duration-200 hover:shadow-md hover:ring-1"
    style={{
      ['--hover-border-color' as string]: branding.primaryColor,
    }}
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <h3 className="text-lg font-semibold" style={{ color: branding.textColor }}>{name}</h3>
        <p className="text-sm mt-1" style={{ color: branding.textColorMuted }}>{description}</p>
      </div>
      {highlighted ? (
        <span
          className="text-xs font-semibold px-2.5 py-1 rounded-full border"
          style={{ backgroundColor: branding.secondaryColor, color: branding.primaryColor, borderColor: branding.secondaryColor }}
        >
          Most popular
        </span>
      ) : null}
    </div>
    <div className="mt-4 flex items-baseline gap-1">
      <span className="text-4xl font-bold" style={{ color: branding.textColor }}>{price}</span>
      <span className="text-sm" style={{ color: branding.textColorMuted }}>{period}</span>
    </div>
    <ul className="mt-6 space-y-3">
      {features.map((feature, i) => (
        <li key={i} className="flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: branding.primaryColor }} />
          <span className="text-sm" style={{ color: branding.textColor }}>{feature}</span>
        </li>
      ))}
    </ul>
    <div className="mt-8">
      <Button
        fullWidth
        variant={buttonVariant}
        onClick={onGetStarted}
      >
        {buttonText}
      </Button>
    </div>
  </div>
);

export default LandingPage;
